import { useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { CanvasCapture, CreditUsageSession } from '@repo/contracts';
import { Skeleton } from '@repo/ui/skeleton';

import { canvasKeys, listProjectCaptures } from '../api/canvas-captures';
import { BillingFrame, CARD, SectionHeader } from '../billing/billing-ui';
import { coversBySession } from '../sessions/session-grid';
import { useCreditUsage } from './credit-usage-api';

dayjs.extend(relativeTime);

const credits = (n: number) => n.toLocaleString();

/** The room's first screen on its pale violet, as on home's cards. */
function Thumb({ capture }: { capture?: CanvasCapture }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative h-12 w-22 shrink-0 overflow-hidden rounded-badge bg-violet-soft">
      {capture ? (
        <div className="absolute inset-x-[8.6%] top-[16%] bottom-0 overflow-hidden rounded-t-tab shadow-soft">
          <img
            alt=""
            className={`size-full object-cover object-top transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            decoding="async"
            onLoad={() => {
              setLoaded(true);
            }}
            src={capture.imageUrl}
          />
        </div>
      ) : null}
    </div>
  );
}

function RoomRow({
  capture,
  room,
}: {
  capture?: CanvasCapture;
  room: CreditUsageSession;
}) {
  return (
    <li className="flex items-center gap-5 py-3">
      <Thumb capture={capture} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-heading text-ui-lg font-bold tracking-tight text-text-primary">
          {room.name}
        </p>
        <p className="truncate text-caption text-text-secondary">
          {room.turns} turn{room.turns === 1 ? '' : 's'} · last{' '}
          {dayjs(room.lastChargedAt).fromNow()}
        </p>
      </div>
      <span className="pr-4 font-heading text-ui-lg font-bold text-text-primary tabular-nums">
        {credits(room.credits)}
      </span>
    </li>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-16 rounded-tile" />
      <Skeleton className="h-16 rounded-tile" />
      <Skeleton className="h-16 rounded-tile" />
    </div>
  );
}

/**
 * "/billing/usage" — where the credits went, room by room.
 *
 * Follows the active scope, like the balance pill: acting as a team shows the
 * team's spending.
 */
export function UsagePage() {
  // Reached from Plans & billing's "Usage" button, back goes there; from the
  // profile menu or a pasted link it goes home, like billing's own back.
  const fromBilling =
    (useLocation().state as { from?: string } | null)?.from === 'billing';
  const { data, error, isLoading } = useCreditUsage();
  const rooms = data?.sessions ?? [];

  // Each room's cover, fetched per site under the same key home's cards use,
  // so coming from home they are already cached.
  const projectIds = [...new Set(rooms.map((room) => room.projectId))];
  const captureQueries = useQueries({
    queries: projectIds.map((projectId) => ({
      queryFn: () => listProjectCaptures(projectId),
      queryKey: canvasKeys.projectCaptures(projectId),
      staleTime: Infinity,
    })),
  });
  const coverOf = coversBySession(
    captureQueries.flatMap((query) => query.data ?? []),
  );

  // Rooms only: charges outside a room (panels, lookalikes) are left out of
  // this page, so the server's ledger total would not match the rows.
  const total = rooms.reduce((sum, room) => sum + room.credits, 0);

  return (
    <BillingFrame
      back={
        fromBilling
          ? {
              label: 'Plans & billing',
              state: { from: 'usage' },
              to: '/billing',
            }
          : undefined
      }
    >
      <section className={`${CARD} mt-6 px-5 pt-5 pb-2`}>
        <SectionHeader>Credits Usage</SectionHeader>
        <p className="mt-1 text-caption text-text-secondary">
          Where {data?.team ? "your team's" : 'your'} credits went, by room.
        </p>

        <div className="mt-4">
          {error ? (
            <p className="mb-3 rounded-button bg-tag-critical px-4 py-3 text-ui font-semibold text-critical">
              Couldn&apos;t load your usage. Try again in a moment.
            </p>
          ) : isLoading || !data ? (
            <PageSkeleton />
          ) : rooms.length === 0 ? (
            <p className="py-8 text-center text-ui text-text-secondary">
              No room has used credits yet. Ask the room a question and it shows
              up here.
            </p>
          ) : (
            <ul className="divide-y divide-hairline">
              {rooms.map((room) => (
                <RoomRow
                  capture={coverOf.get(room.sessionId)}
                  key={room.sessionId}
                  room={room}
                />
              ))}
              <li className="flex items-center gap-5 py-4">
                <p className="flex-1 text-ui-strong font-semibold text-text-secondary">
                  Total
                </p>
                <span className="pr-4 font-heading text-ui-lg font-bold text-text-primary tabular-nums">
                  {credits(total)}
                </span>
              </li>
            </ul>
          )}
        </div>
      </section>
    </BillingFrame>
  );
}
